import {AxiosResponse} from 'axios'
import {HttpInstance} from '../createHttpInstance'
import {FaceOffRun, FaceOffRunListEntry, FaceOffRunsResponse} from './types'

export const FACE_OFF_RUNS_URL = '/v2/faces/faceoff/runs'

/** Lists the Face Off harness runs. The body is untrusted until decoded. */
export const getFaceOffRuns = (
  http: HttpInstance,
): Promise<AxiosResponse<unknown>> =>
  http.request<unknown>({method: 'GET', url: FACE_OFF_RUNS_URL})

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

export const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

export const decodeRun = (raw: unknown, index: number): FaceOffRun => {
  if (
    !isRecord(raw) ||
    typeof raw.runId !== 'string' ||
    typeof raw.startedAt !== 'string' ||
    Number.isNaN(Date.parse(raw.startedAt)) ||
    !isFiniteNumber(raw.goldenVersion) ||
    !isFiniteNumber(raw.caseCount) ||
    !isFiniteNumber(raw.flipCount)
  ) {
    throw new Error(`Malformed Face Off run at index ${index}`)
  }
  return {
    runId: raw.runId,
    startedAt: raw.startedAt,
    goldenVersion: raw.goldenVersion,
    caseCount: raw.caseCount,
    flipCount: raw.flipCount,
  }
}

const optionalNumber = (
  raw: Record<string, unknown>,
  key: string,
  index: number,
): number | null => {
  const value = raw[key]
  if (value === undefined || value === null) return null
  if (!isFiniteNumber(value)) {
    throw new Error(`Malformed Face Off run at index ${index}`)
  }
  return value
}

/**
 * One run-list entry. The counts and the re-baseline fields are newer; an
 * older media leaves them out, and the api layer works out re-baselines.
 */
export const decodeRunListEntry = (
  raw: unknown,
  index: number,
): FaceOffRunListEntry => {
  const run = decodeRun(raw, index)
  const r = raw as Record<string, unknown>
  if (r.rebaseline !== undefined && typeof r.rebaseline !== 'boolean') {
    throw new Error(`Malformed Face Off run at index ${index}`)
  }
  return {
    ...run,
    regressions: optionalNumber(r, 'regressions', index),
    improvements: optionalNumber(r, 'improvements', index),
    shiftCount: optionalNumber(r, 'shiftCount', index),
    passed: optionalNumber(r, 'passed', index),
    total: optionalNumber(r, 'total', index),
    passRate: optionalNumber(r, 'passRate', index),
    rebaseline: r.rebaseline === undefined ? null : r.rebaseline,
    baselineFrom: optionalNumber(r, 'baselineFrom', index),
  }
}

/**
 * Decodes the run-list body. One missing or mistyped field rejects the whole
 * response, so the page never shows a partial or half-typed run list.
 */
export const decodeFaceOffRuns = (body: unknown): FaceOffRunsResponse => {
  if (!isRecord(body) || !Array.isArray(body.runs)) {
    throw new Error('Malformed Face Off runs response')
  }
  return {runs: body.runs.map(decodeRunListEntry)}
}
